import { useMemo, useRef } from "react";
import { Card, Flex, Row, Col, Skeleton, InputRef } from "antd";
import { TagsOutlined, CheckCircleOutlined, CrownOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { getPackagePromoOverview } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatCurrency, formatUpgrades } from "shared/utils";
import { promoStatusOfDates } from "pages/products/component/PromoStatusBadge";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { PackageAnalyticsRow, PackageType } from "shared/types";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { now } from "shared/utils/dayjs";
import { MAX_PDF_ROWS } from "pdf/layout";
import { countPromoProducts } from "shared/utils/promo-count";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { formatDateRangeOrDash } from "shared/reports/columnCatalogue";
import { pendapatanPromoSpec } from "pdf/reports/specs/archetype1";
import { useReportPageCount } from "./useReportPageCount";
import { ProductStatusTag } from "./ProductNameCell";
import { textPx } from "shared/constants/zoom";

interface PendapatanProdukPromoContentProps {
  type: PackageType;
  title: string;
  backTo: string;
}

// Financial-lens successor to the retired Performa Paket/Membership (Promo)
// report — same always-promo, always-all-time shape (a promo's own
// promo_starts_at/promo_ends_at already describes its real window; see
// getPackagePromoOverview), but drops the live-snapshot columns that don't
// hold a stable answer for a completed campaign (Aktif/Kredit Tersisa/Akan
// Kedaluwarsa — all present-state, not "how did this campaign do") in favor
// of Klien Unik/Penjualan/Kredit Terpakai/Pendapatan dari Booking (all
// backward-looking, same category as the non-promo report's columns), and
// replaces the count-based best-seller KPI with the revenue-based one.
const PendapatanProdukPromoContent = ({
  type,
  title,
  backTo,
}: PendapatanProdukPromoContentProps) => {
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  const searchInput = useRef<InputRef>(null);
  const { data: overview, isLoading } = getPackagePromoOverview(type);

  // Total/Active are derived client-side from `table` rather than added as
  // new backend aggregate fields. Rows are per TIER and a promo product can
  // hold several, so both count distinct promo products, not rows.
  const totalPromo = useMemo(
    () => countPromoProducts(overview?.table ?? []),
    [overview],
  );
  const activePromo = useMemo(
    () => countPromoProducts(overview?.table ?? [], { activeOnly: true }),
    [overview],
  );

  // Columns come from the shared report catalogue (one name, width and
  // alignment per metric); this page keeps its own order, search and sorters.
  const columns: ReportColumn<PackageAnalyticsRow>[] = [
    reportColumn<PackageAnalyticsRow>(type === "membership" ? "membership" : "package", {
      dataIndex: "name",
      render: (name: string, row) => {
        // A promo no longer on sale (ended, or deleted from the catalogue)
        // reads muted.
        const notSelling = promoStatusOfDates(row) === "ended" || !!row.is_deleted;
        return (
          <Flex gap={8} align="center" wrap="wrap">
            <span style={{ fontWeight: 600, color: notSelling ? colors.ink3 : undefined }}>
              {name}
            </span>
            {/* The report chip: the status word only, the same words the PDF
                prints under the name. The catalogue's own chip adds the date
                ("Promo · Until 23 Aug"), which a table column has no room
                for and the Periode Promo column already carries. */}
            <ProductStatusTag row={row} />
          </Flex>
        );
      },
      search: getColumnSearchProps<PackageAnalyticsRow, "name">({
        config: "name",
        searchInput,
      }),
    }),
    reportColumn<PackageAnalyticsRow>("promo_period", {
      // Default sort — this report has no date-range picker (always
      // "Sepanjang waktu"), so ranking by revenue doesn't carry the usual
      // "did I pick the right window" framing, and Promo Terlaris already
      // surfaces the top performer as its own KPI card above. A
      // chronological view (most recent promo first, same convention as
      // Pembelian's purchase-date default) is the more natural default for
      // browsing a promo history. dataIndex (not just `key`) so AntD's
      // sorter event reports this column via `field`, mirroring the sort
      // into the PDF export.
      dataIndex: "promo_starts_at",
      render: (_, row) => formatDateRangeOrDash(row.promo_starts_at, row.promo_ends_at),
      sorter: (a, b) => (a.promo_starts_at ?? "").localeCompare(b.promo_starts_at ?? ""),
      defaultSortOrder: "descend",
    }),
    reportColumn<PackageAnalyticsRow>("unique_clients", {
      dataIndex: "unique_clients",
      sorter: (a, b) => a.unique_clients - b.unique_clients,
    }),
    reportColumn<PackageAnalyticsRow>("sold", {
      dataIndex: "sold",
      // Real sales only. Upgrades are counted separately: they are
      // expansion revenue rather than new units, but their fee is in
      // Penjualan and would otherwise have no visible source.
      info: () => translate("analyticsPage.package.col_sold_info"),
      sorter: (a, b) => a.sold - b.sold,
    }),
    // Always present, and paket only. Same reasoning as the non-promo report:
    // a column that appears with the data has to be gated identically on
    // three surfaces, and the PDF row cap made it disagree with the screen.
    // A dash means no upgrades in this period.
    ...(type === "membership"
      ? []
      : [
          reportColumn<PackageAnalyticsRow>("upgrades", {
            dataIndex: "upgrades",
            render: (value: number) => <span style={tnum}>{formatUpgrades(value)}</span>,
            sorter: (a, b) => (a.upgrades ?? 0) - (b.upgrades ?? 0),
          }),
        ]),
    reportColumn<PackageAnalyticsRow>("sales", {
      dataIndex: "sales",
      sorter: (a, b) => a.sales - b.sales,
    }),
    // Paket only — Membership has no credit-consumption concept. Same paired
    // columns as the non-promo Pendapatan Dari Paket report (Kredit Terpakai
    // is the raw count, Pendapatan dari Booking its prorated $ value) — a
    // promo campaign's actual usage is just as much "how did this campaign
    // do" as Klien Unik/Penjualan already on this row, unlike the
    // live-snapshot columns (Aktif/Kredit Tersisa) this report deliberately
    // drops.
    ...(type === "membership"
      ? []
      : [
          reportColumn<PackageAnalyticsRow>("credits_used", {
            dataIndex: "credits_used",
            sorter: (a, b) => a.credits_used - b.credits_used,
          }),
          reportColumn<PackageAnalyticsRow>("booking_revenue", {
            dataIndex: "earned_revenue",
            sorter: (a, b) => a.earned_revenue - b.earned_revenue,
          }),
        ]),
    reportColumn<PackageAnalyticsRow>("last_purchase", {
      dataIndex: "last_purchase",
      sorter: (a, b) => (a.last_purchase ?? "").localeCompare(b.last_purchase ?? ""),
    }),
  ];

  // This report is always all-time, and the server's range_label for it is
  // Indonesian ("Sepanjang Waktu"); render the translated label instead, on
  // screen and in the PDF.
  const rangeLabelDisplay = translate("analyticsPage.package.compare_mode_all_time");

  const pdfSpec = useMemo(() => pendapatanPromoSpec(type), [type]);
  const pageCount = useReportPageCount(pdfSpec, overview, sortState);

  return (
    <>
      <ReportPageHeading
        title={title}
        backTo={backTo}
        extra={
          <ExportMenu
            reportSlug={`pendapatan-${type}-promo`}
            params={{ promo: "promo", all_time: "true" }}
            pdf={
              overview && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(overview.table.length, MAX_PDF_ROWS),
                  total: overview.table.length,
                },
                fileName: `spott-pendapatan-${type}-promo-${now().format("YYYYMMDD")}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug:
                      type === "membership"
                        ? "pendapatan-membership-promo"
                        : "pendapatan-paket-promo",
                    studio: authState.business?.name ?? "",
                    data: { ...overview, range_label: rangeLabelDisplay },
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <span style={{ fontSize: textPx(13), color: colors.ink2 }}>{rangeLabelDisplay}</span>
        <Row gutter={[16, 16]}>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Col key={i} xs={24} sm={12} lg={8}>
                <Card style={{ borderRadius: 16 }}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))
          ) : (
            <>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<TagsOutlined />}
                  title={translate("analyticsPage.package.kpi_promo_total")}
                  value={totalPromo}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<CheckCircleOutlined />}
                  title={translate("analyticsPage.package.kpi_promo_active")}
                  value={activePromo}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<CrownOutlined />}
                  title={
                    overview?.best_seller_by_revenue.revenue
                      ? `${translate("analyticsPage.package.kpi_promo_best_seller_revenue")} · ${formatCurrency(overview.best_seller_by_revenue.revenue)}`
                      : translate("analyticsPage.package.kpi_promo_best_seller_revenue")
                  }
                  value={overview?.best_seller_by_revenue.name ?? "-"}
                  text
                />
              </Col>
            </>
          )}
        </Row>
        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<PackageAnalyticsRow>
            rowKey="package_id"
            loading={isLoading}
            columns={columns}
            dataSource={overview?.table ?? []}
            pageMemory={`analyticsPendapatanPromo.${type}`}
            onSortChange={onSortChange}
          />
        </Card>
      </Flex>
    </>
  );
};

export default PendapatanProdukPromoContent;
